import { Controller } from '@hotwired/stimulus'
import { formatPrice } from '@/utils/functions'
import { mercureSubscribe, mercureUnsubscribe } from '../../../service/mercure'
import { AlertType } from '@/public/components/Alert/types'
import LotCompletionTimeController from '@/public/blocks/LotCompletionTime/controller'
import PriceInputController from '@/public/behaviors/PriceInput'
import BidConfirmModalController, { BidOutbidDetail } from '@/public/blocks/BidConfirmModal/controller'
import InputController from '@/public/components/Input/controller'

/* stimulusFetch: 'lazy' */
export default class extends Controller {
    static outlets = ['price-input', 'modal', 'lot-completion-time']
    static targets = [
        'slider',
        'sliderNext',
        'sliderPrev',
        'thumb',
        'moreThumbsButton',
        'currentBidPrice',
        'yourBidPrice',
        'yourBidPriceWrapper',
        'avgPriceDropdownWrapper',
        'avgPriceDropdown',
        'badgeLeading',
        'badgeOutbid'
    ]
    static values = {
        mercureUrl: String,
        currentPrice: Number,
        lotNumber: Number,
        userHash: String,
        isLeading: Boolean,
        userLastBid: Number,
        type: String,
    }

    private swiper: any | null = null
    private sendingRequest = false
    private currentPrice: number = 0
    private tabletMediaQuery: MediaQueryList | null = null
    private inputController: InputController | null = null
    private isLeading: boolean = false
    private userLastBid: number | null = null

    declare readonly priceInputOutlet: PriceInputController
    declare readonly hasPriceInputOutlet: boolean
    declare readonly lotCompletionTimeOutlets: LotCompletionTimeController[]
    declare readonly hasLotCompletionTimeOutlet: boolean

    declare readonly mercureUrlValue: string
    declare readonly hasMercureUrlValue: boolean
    declare readonly currentPriceValue: number
    declare readonly lotNumberValue: number
    declare readonly userHashValue: string
    declare readonly hasUserHashValue: boolean
    declare readonly isLeadingValue: boolean
    declare readonly userLastBidValue: number
    declare readonly typeValue: string

    declare readonly sliderTarget: HTMLElement
    declare readonly sliderNextTarget: HTMLElement
    declare readonly sliderPrevTarget: HTMLElement
    declare readonly thumbTargets: HTMLElement[]
    declare readonly currentBidPriceTarget: HTMLElement
    declare readonly hasCurrentBidPriceTarget: boolean
    declare readonly yourBidPriceTarget: HTMLElement
    declare readonly hasYourBidPriceTarget: boolean
    declare readonly yourBidPriceWrapperTarget: HTMLElement
    declare readonly hasYourBidPriceWrapperTarget: boolean
    declare readonly avgPriceDropdownWrapperTarget: HTMLElement
    declare readonly hasAvgPriceDropdownWrapperTarget: boolean
    declare readonly avgPriceDropdownTarget: HTMLElement
    declare readonly hasAvgPriceDropdownTarget: boolean
    declare readonly badgeLeadingTargets: HTMLElement[]
    declare readonly hasBadgeLeadingTarget: boolean
    declare readonly badgeOutbidTargets: HTMLElement[]
    declare readonly hasBadgeOutbidTarget: boolean

    get isBiddable(): boolean {
        return this.typeValue === 'open' || this.typeValue === 'express'
    }

    connect() {
        window.addEventListener('bid:placed', this.handleBidPlaced as EventListener)
        window.addEventListener('bid:outbid', this.handleBidOutbid as EventListener)

        this.currentPrice = this.currentPriceValue ? this.currentPriceValue : 0
        this.isLeading = this.isLeadingValue ?? false
        this.userLastBid = this.userLastBidValue ? this.userLastBidValue : null

        if (this.isBiddable) {
            this.updateBidBadges()
        }

        if (this.hasMercureUrlValue && this.mercureUrlValue) {
            requestAnimationFrame(() => requestAnimationFrame(() => mercureSubscribe(this.mercureUrlValue, this.handleMercureMessage)))
        }

        if (this.hasAvgPriceDropdownWrapperTarget) {
            this.avgPriceDropdownWrapperTarget.addEventListener('mouseenter', this.handleAvgPriceDropdownDisplay)
        }

        if (this.hasPriceInputOutlet) {
            this.element.addEventListener('price-input:change', this.handlePriceChange as EventListener)
        }
    }

    disconnect() {
        window.removeEventListener('bid:placed', this.handleBidPlaced as EventListener)
        window.removeEventListener('bid:outbid', this.handleBidOutbid as EventListener)

        if (this.hasMercureUrlValue) {
            mercureUnsubscribe(this.mercureUrlValue, this.handleMercureMessage)
        }

        if (this.hasAvgPriceDropdownWrapperTarget) {
            this.avgPriceDropdownWrapperTarget.removeEventListener('mouseenter', this.handleAvgPriceDropdownDisplay)
        }

        if (this.hasPriceInputOutlet) {
            this.element.removeEventListener('price-input:change', this.handlePriceChange as EventListener)
        }
    }

    async sliderTargetConnected(container: HTMLElement) {
        this.tabletMediaQuery = matchMedia('(min-width: 768px)')
        this.tabletMediaQuery.addEventListener('change', this.handleTabletMediaQueryChange)

        if (this.tabletMediaQuery?.matches) {
            this.initSlider()
        }

        const { Fancybox } = await import('@fancyapps/ui')

        // @ts-ignore
        await import('@fancyapps/ui/dist/fancybox/fancybox.css')
        Fancybox.bind('[data-fancybox]')
    }

    sliderTargetDisconnected(container: HTMLElement) {
        this.swiper?.destroy()
    }

    thumbTargetConnected(element: HTMLElement) {
        element.addEventListener('click', (event: Event) => {
            const target = event.target as HTMLElement
            const indexText = target?.dataset?.index
            if (!indexText) {
                return
            }

            const index = parseInt(indexText, 10)

            if (Number.isNaN(index)) {
                return
            }

            this.swiper?.slideTo(index)
        })
    }

    moreThumbsButtonTargetConnected(button: HTMLElement) {
        button.addEventListener('click', () => {
            this.thumbTargets.forEach((thumb) => thumb.classList.remove('hidden'))
            button.remove()
        })
    }

    handleTabletMediaQueryChange = () => {
        if (this.tabletMediaQuery?.matches) {
            this.initSlider()
        } else {
            this.swiper?.destroy()
        }
    }

    handleAvgPriceDropdownDisplay = () => {
        if (!this.hasAvgPriceDropdownTarget) {
            return
        }

        const dropdown = this.avgPriceDropdownTarget

        dropdown.removeAttribute('style')

        const dropdownRect = dropdown.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const padding = 16

        if (viewportWidth < (dropdownRect.right + padding)) {
            dropdown.style.right = `0px`
            dropdown.style.left = `auto`
            dropdown.style.transform = `translateX(0)`
        } else {
            dropdown.removeAttribute('style')
        }
    }

    private handlePriceChange = (event: CustomEvent) => {
        this.clearInputError()
    }

    handleSubmit(event: Event) {
        event.preventDefault()
        event.stopPropagation()

        if (this.sendingRequest || !this.hasPriceInputOutlet) {
            return
        }

        const bid = this.priceInputOutlet.getNumericValue()

        if (!bid) {
            return this.setInputError('Укажите ставку')
        }

        const modalElement = document.getElementById('bid-confirm-modal')

        if (!modalElement) {
            return
        }

        const controller = this.application.getControllerForElementAndIdentifier(
            modalElement,
            'bid-confirm-modal'
        ) as BidConfirmModalController | null

        if (!controller) {
            return
        }

        controller.open(this.lotNumberValue, bid)
    }

    handleDecrease(event: Event) {
        if (!this.hasPriceInputOutlet) {
            return
        }

        this.priceInputOutlet.decrease(event)
    }

    handleIncrease(event: Event) {
        if (!this.hasPriceInputOutlet) {
            return
        }

        this.priceInputOutlet.increase(event)
    }

    async handleBuyout() {
        if (this.sendingRequest) {
            return
        }

        this.sendingRequest = true

        try {
            const uri = new URL(location.href)
            const response = await fetch(`${uri.pathname}/buyout`, {
                method: 'POST',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            })
            const { error, success } = await response.json()

            this.sendingRequest = false

            if (!response.ok || !success) {
                return this.showAlert(error || 'К сожалению, произошена ошибка при попытке выкупить лот, повторите попытку позже', 'error')
            }

            location.assign('/lots')
        } catch {
            this.sendingRequest = false
            this.showAlert('К сожалению, произошена ошибка при попытке выкупить лот, повторите попытку позже', 'error')
        }
    }

    handleMercureMessage = (event: MessageEvent) => {
        try {
            const data = JSON.parse(event.data)
            const eventType = data?.event
            const payload = data?.payload || {}

            if (data?.type === 'auction' && eventType === 'price_changed') {
                return this.handlePriceChanged(payload)
            }

            if (data?.type === 'auction' && eventType === 'expiration_changed') {
                return this.handleExpirationChanged(payload)
            }

            if (data?.type === 'bid_status' && eventType === 'bid_status_changed') {
                this.handleBidStatusChanged(payload)
            }
        } catch {}
    }

    private handlePriceChanged(payload: { lotNumber?: number; price?: number; html?: string; leaderUserHash?: string }) {
        const lotNumber = payload?.lotNumber
        const price = payload?.price
        const html = payload?.html
        const leaderUserHash = payload?.leaderUserHash

        if (String(lotNumber) !== String(this.lotNumberValue)) {
            return
        }

        if (price != null && price !== this.currentPrice && html) {
            this.updateCurrentPrice(price, html);
        }

        if (!this.isBiddable || !this.hasUserHashValue) {
            return
        }

        this.isLeading = this.userHashValue === leaderUserHash

        if (this.isLeading && price != null) {
            this.userLastBid = price
        }

        this.updateBidBadges()
    }

    private handleBidStatusChanged(payload: { lotNumber?: number; isLeading?: boolean; currentPrice?: number; userLastBid?: number }) {
        const lotNumber = payload?.lotNumber

        if (String(lotNumber) !== String(this.lotNumberValue)) {
            return
        }

        if (payload?.isLeading !== undefined) {
            this.isLeading = payload.isLeading
        }

        if (payload?.currentPrice != null) {
            this.currentPrice = payload.currentPrice
            this.updatCurrentBidPrice(formatPrice(payload.currentPrice))
        }

        if (payload?.userLastBid !== undefined) {
            this.userLastBid = payload.userLastBid > 0 ? payload.userLastBid : null
        }

        this.updateBidBadges()
    }

    private handleExpirationChanged(payload: { lotNumber?: number; expirationTime?: number }) {
        const timestamp = payload?.expirationTime

        if (!timestamp || !this.hasLotCompletionTimeOutlet) {
            return
        }

        this.lotCompletionTimeOutlets.forEach((lotCompletionTime) => lotCompletionTime.updateTimestamp(timestamp))
    }

    private updateBidBadges() {
        if (!this.isBiddable) {
            return
        }

        const participating = this.userLastBid != null && this.userLastBid > 0

        if (!participating) {
            this.badgeLeadingTargets?.forEach((element: HTMLElement) => element.classList.add('hidden'))
            this.badgeOutbidTargets?.forEach((element: HTMLElement) => element.classList.add('hidden'))
            return
        }

        if (this.isLeading) {
            this.badgeLeadingTargets?.forEach((element: HTMLElement) => element.classList.remove('hidden'))
            this.badgeOutbidTargets?.forEach((element: HTMLElement) => element.classList.add('hidden'))
        } else {
            this.badgeLeadingTargets?.forEach((element: HTMLElement) => element.classList.add('hidden'))
            this.badgeOutbidTargets?.forEach((element: HTMLElement) => element.classList.remove('hidden'))
        }
    }

    private handleBidPlaced = (event: CustomEvent) => {
        const detail = event.detail as { lotNumber?: number; price?: number }
        const lotNumber = detail?.lotNumber
        const price = detail?.price

        if (!lotNumber || !price) {
            return
        }

        if (String(lotNumber) !== String(this.lotNumberValue)) {
            return
        }

        this.currentPrice = price
        this.isLeading = true
        this.userLastBid = price

        this.updateYourBidPrice(price)
        this.updatCurrentBidPrice(formatPrice(price))

        if (this.isBiddable) {
            this.updateBidBadges()
        }
    }

    private handleBidOutbid = (event: CustomEvent) => {
        const detail = event.detail as BidOutbidDetail
        const lotNumber = detail?.lotNumber
        const price = detail?.price
        const leaderUserHash = detail?.leaderUserHash

        if (!lotNumber || price == null) {
            return
        }

        if (String(lotNumber) !== String(this.lotNumberValue)) {
            return
        }

        this.currentPrice = price
        this.updatCurrentBidPrice(formatPrice(price))

        if (!this.isBiddable || !this.hasUserHashValue) {
            return
        }

        this.isLeading = this.userHashValue === leaderUserHash
        this.updateBidBadges()
    }

    private async initSlider() {
        const [{ default: Swiper }, { Navigation }] = await Promise.all([
            import('swiper'),
            import('swiper/modules')
        ])

        // @ts-ignore
        await import('swiper/swiper-bundle.css')

        this.swiper = new Swiper(this.sliderTarget, {
            modules: [Navigation],
            spaceBetween: 24,
            grabCursor: true,
            slidesPerView: 1,
            loop: true,
            navigation: {
                nextEl: this.sliderNextTarget,
                prevEl: this.sliderPrevTarget
            },
            autoHeight: true
        })
    }

    private updateCurrentPrice(price: number, html: string) {
        this.currentPrice = price

        if (this.hasPriceInputOutlet) {
            this.priceInputOutlet.setCurrentPrice(price)
        }

        if (this.hasCurrentBidPriceTarget) {
            this.currentBidPriceTarget.innerHTML = html
        }
    }

    private updatCurrentBidPrice(html: string) {
        if (this.hasCurrentBidPriceTarget) {
            this.currentBidPriceTarget.innerHTML = html
        }
    }

    private updateYourBidPrice(newPrice: number) {
        if (!this.hasYourBidPriceTarget) {
            return
        }

        this.yourBidPriceTarget.innerHTML = formatPrice(newPrice)

        if (this.hasYourBidPriceWrapperTarget) {
            this.yourBidPriceWrapperTarget.classList.remove('hidden')
        }
    }

    private setInputError(error: string) {
        const inputController = this.resolveInputController()

        if (!inputController) {
            return
        }

        inputController.setError(error)
    }

    private clearInputError() {
        const inputController = this.resolveInputController()

        if (!inputController) {
            return
        }

        inputController.clearError()
    }

    private showAlert(message: string, type: AlertType, timeout?: number) {
        window.dispatchEvent(new CustomEvent('alert:show', { detail: { message, type, timeout }, bubbles: true }))
    }

    private resolveInputController(): InputController|null
    {
        if (!this.inputController && this.hasPriceInputOutlet) {
            this.inputController = this.application.getControllerForElementAndIdentifier(
                this.priceInputOutlet.element,
                'input'
            ) as InputController | null
        }

        return this.inputController
    }
}
